'use client';

import { useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { getPageTypeConfig } from '../components/theme/TopNavStyle';
import useIsMobile from './useIsMobile';

// Configuration constants matching TopNavStyle.jsx
const TOP_NAV_HEIGHT_CONFIG = {
  desktop: {
    withBannerAndTopNav: 150,
    withOnlyTopNav: 70,
    hasTopBanner: 100,
    isPagesWithOnlyTopNavAndFill: 68,
    default: 0,
  },
  mobile: {
    withBannerAndTopNav: 150,
    withOnlyTopNav: 56,
    hasTopBanner: 80,
    isPagesWithOnlyTopNavAndFill: 56,
    default: 0,
  },
};

/**
 * Calculate expected navigation height based on page configuration
 * @param {Object} config - Page type configuration
 * @param {boolean} isMobile - Whether the device is mobile
 * @returns {number} Expected height in pixels
 */
const calculateExpectedHeight = (config, isMobile) => {
  const heightConfig = TOP_NAV_HEIGHT_CONFIG[isMobile ? 'mobile' : 'desktop'];

  // Priority order for height calculation
  const priorities = [
    'withBannerAndTopNav',
    'withOnlyTopNav',
    'hasTopBanner',
    'isPagesWithOnlyTopNavAndFill',
  ];

  // Special case: Explore page with banner
  if (config.showBannerOnExplorePage && config.hasTopBanner) {
    return heightConfig.hasTopBanner;
  }

  // Check conditions in priority order
  const matchedCondition = priorities.find(
    (condition) => config[condition] && heightConfig[condition] !== undefined,
  );

  return matchedCondition ? heightConfig[matchedCondition] : heightConfig.default;
};

/**
 * Enhanced custom hook to get the height of the main-top-nav element
 * Combines DOM measurement with configuration-based fallbacks for better reliability
 */
const useTopNavHeight = () => {
  const [topNavHeight, setTopNavHeight] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!isMobile) {
      setTopNavHeight(0);
      setIsReady(true);
      return undefined;
    }

    // Get page configuration for fallback height calculation
    const pageConfig = getPageTypeConfig(pathname, searchParams);
    const expectedHeight = calculateExpectedHeight(pageConfig, isMobile);

    const updateNavHeight = () => {
      const navElement = document.getElementById('main-top-nav');
      if (navElement) {
        // First try to get the height from the wrapper
        let height = navElement.offsetHeight;

        // If wrapper has no height, look for the actual header child
        if (height === 0) {
          const headerChild = navElement.querySelector('header, [class*="MuiAppBar"]');
          if (headerChild) {
            height = headerChild.offsetHeight;
          }
        }

        // If still no height, try any child with significant height
        if (height === 0 && navElement.children) {
          const children = Array.from(navElement.children);
          const tallestChild = children.reduce((tallest, child) => {
            return child.offsetHeight > (tallest?.offsetHeight || 0) ? child : tallest;
          }, null);

          if (tallestChild && tallestChild.offsetHeight > 0) {
            height = tallestChild.offsetHeight;
          }
        }

        setTopNavHeight(height);
        setIsReady(true);
      } else {
        // Fallback to expected height if element not found
        setTopNavHeight(expectedHeight);
        setIsReady(true);
      }
    };

    // Initial measurement with a small delay to ensure DOM is ready
    const initialTimer = setTimeout(updateNavHeight, 100);

    // Update on resize
    const handleResize = () => {
      updateNavHeight();
    };

    // Update on DOM changes (in case nav content changes)
    const observer = new MutationObserver(() => {
      updateNavHeight();
    });

    const navElement = document.getElementById('main-top-nav');
    if (navElement) {
      observer.observe(navElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'], // Only observe relevant attributes
      });
    }

    window.addEventListener('resize', handleResize);

    // Also listen for orientation changes on mobile
    window.addEventListener('orientationchange', handleResize);

    // Cleanup
    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      observer.disconnect();
    };
  }, [isMobile, pathname, searchParams]);

  return {
    height: isMobile ? topNavHeight : 0,
    isReady,
  };
};

export default useTopNavHeight;
